import { useState, useEffect, useCallback, useRef } from "react"
import debounce from "lodash/debounce"
import kebabCase from "lodash/kebabCase"
import isObject from "lodash/isObject"
import mapKeys from "lodash/mapKeys"
import noop from "lodash/noop"
import {
  useQueryParams,
  StringParam,
  NumberParam,
  JsonParam,
} from "use-query-params"

/**
 * @description возвращает задебаунсеный value
 *
 * @param {any} value
 * @param {number} delay
 * @returns {any}
 */
export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

/**
 *  Transform object's keys to kebab case
 *  @param obj - the object to transform
 */
export const renameObjKeysToKebabCase = obj => {
  if (isObject(obj)) {
    return mapKeys(obj, (val, key) => kebabCase(key))
  }
  return obj
}

/**
 * @description Хук связанного состояния пагинации и поиска
 *
 * @returns {
 *   {
 *     handlers: {setQuery: Function},
 *     values: {
 *       query: {filter: string, perPage: number, page: number, sorted: object},
 *       searchInputValue: string,
 *     },
 *     state: {number}
 *   }
 * }
 */
export const useQueryState = () => {
  const [query, setQuery] = useQueryParams({
    page: NumberParam,
    "page-size": NumberParam,
    "order-by": JsonParam,
    filter: StringParam,
  })
  const { page, "page-size": pageSize, "order-by": orderBy, filter } = query

  // Dummy state to cause rerenders
  const [state, setState] = useState(0)

  const setQueryKebab = debounce(
    newQuery => {
      if (query.filter !== newQuery.filter) {
        setQuery(renameObjKeysToKebabCase(newQuery), `replaceIn`)
        setState(Math.random())
        return
      }
      setQuery(renameObjKeysToKebabCase(newQuery), `pushIn`)
      setState(Math.random())
    },
    500,
    { maxWait: 1500 },
  )

  return {
    query: {
      page: page || 1,
      pageSize: pageSize || 50,
      orderBy,
      filter: filter || ``,
    },
    handlers: {
      setQuery: setQueryKebab,
    },
    state,
  }
}

/**
 * @description Возврат на последнюю непустую страницу
 * если текущая слишком далеко впереди и поэтому пустая
 *
 * @param params
 */
export const useJumpToItems = params => {
  const { status, items, page, count, perPage, onJump } = params

  useEffect(() => {
    if (status.completed && items.length === 0 && page > 1 && count > 0) {
      const pages = count / perPage
      const pagesFloor = Math.floor(pages)
      const lastNonEmptyPage =
        pagesFloor === pages ? pagesFloor : pagesFloor + 1
      onJump(lastNonEmptyPage)
    }
  }, [status.completed])
}

/**
 * @description Для более краткой работы с состояниями запросов
 *
 * @param {object} status
 * @param {boolean} hold
 * @param {function} onComplete
 * @param {function} onFail
 * @param {function} onPending
 */
export const useStatus = (
  status,
  { hold = false, onComplete = noop, onFail = noop, onPending = noop } = {},
) => {
  useEffect(() => {
    if (!hold && status.completed) {
      onComplete()
    }
    if (!hold && status.failed) {
      onFail()
    }
    if (!hold && status.pending) {
      onPending()
    }
  }, [status])
}

/**
 * @description Возвращает текущий таймштамп и позволяет его обновлять
 *
 * @returns {any[]}
 */
export const useNow = () => {
  const [timestamp, setTimestamp] = useState(Date.now())

  const refresh = useCallback(() => setTimestamp(Date.now()), [])

  return [timestamp, refresh]
}

/**
 * @description Возвращает предыдущее значение
 *
 * @returns {any}
 */
export const usePrevious = value => {
  const ref = useRef()

  useEffect(() => {
    ref.current = value
  }, [value])

  return ref.current
}
